import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
export { resolveLucideIcon as resolveIcon } from "@/lib/iconRegistry";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const calcRelativeTimeFromDiff = (diff: number) => {
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) {
    return `${days}d`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else if (minutes > 0) {
    return `${minutes}m`;
  } else {
    return `${seconds}s`;
  }
};

export const formatTimestamp = (date: Date): string => {
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

/**
 * Flattens a nested object structure by extracting values from arrays and nested objects.
 * This is particularly useful for extracting data from complex API responses.
 *
 * @param obj - The object to flatten
 * @param maxDepth - Maximum recursion depth to prevent infinite loops (default: 5)
 * @returns A flattened object with primitive values
 */
export function flattenObject(obj: unknown, maxDepth: number = 5): Record<string, unknown> {
  if (maxDepth <= 0 || obj === null || obj === undefined) {
    return {};
  }

  function flatten(current: unknown, depth: number): Record<string, unknown> {
    if (depth <= 0 || current === null || current === undefined) {
      return {};
    }

    const flatResult: Record<string, unknown> = {};

    if (Array.isArray(current)) {
      // For arrays, flatten each element and merge results
      current.forEach((item, index) => {
        if (typeof item === "object" && item !== null) {
          const flattened = flatten(item, depth - 1);
          Object.assign(flatResult, flattened);
        } else if (item !== null && item !== undefined) {
          flatResult[`item_${index}`] = item;
        }
      });
    } else if (typeof current === "object") {
      // For objects, recursively flatten
      for (const [key, value] of Object.entries(current)) {
        if (value === null || value === undefined) {
          continue;
        }

        if (typeof value === "object") {
          if (Array.isArray(value)) {
            // Handle arrays
            value.forEach((item, index) => {
              if (typeof item === "object" && item !== null) {
                const flattened = flatten(item, depth - 1);
                Object.assign(flatResult, flattened);
              } else if (item !== null && item !== undefined) {
                flatResult[`${key}_${index}`] = item;
              }
            });
          } else {
            // Handle nested objects
            const flattened = flatten(value, depth - 1);
            Object.assign(flatResult, flattened);
          }
        } else {
          // Handle primitive values
          flatResult[key] = value;
        }
      }
    } else {
      // Handle primitive values at root level
      return { value: current };
    }

    return flatResult;
  }

  return flatten(obj, maxDepth);
}

/**
 * Checks if a string is a valid HTTP or HTTPS URL
 * @param value - The string to check
 * @returns true if the string is a valid URL, false otherwise
 */
export function isUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Handles keyboard activation of a handler.
 * @param e - The keyboard event
 * @param handler - The handler to activate
 */
export function handleKeyboardActivation(e: React.KeyboardEvent, handler: () => void) {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    handler();
  }
}
